import { makeStyles, tokens } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const BREADCRUMB_FONT_SIZE = "12px";
const RAG_DOT_SIZE = "14px";
const RAG_STATUS_INPUT_FONT_SIZE = "15px";
const RAG_GREEN_SHADOW = "rgba(31, 138, 91, 0.4)";
const RAG_AMBER_SHADOW = "rgba(224, 168, 46, 0.4)";
const RAG_RED_SHADOW = "rgba(209, 52, 56, 0.4)";
const RAID_PILL_FONT_SIZE = "11px";
const INFO_BANNER_FONT_SIZE = "12.5px";

export const useWorkspacePlanStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: projectIqTokens.bg,
    fontFamily: projectIqTokens.fontFamily,
    position: "relative",
  },
  saveCenterToast: {
    position: "absolute",
    bottom: "32px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: `8px 18px`,
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: projectIqTokens.shadow,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
  saveCenterToastError: {
    color: tokens.colorPaletteRedForeground1,
    border: `1px solid ${tokens.colorPaletteRedForeground1}`,
  },
  body: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: designTokens.spacing[8],
  },
  content: {
    maxWidth: "1120px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  infoBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "11px",
    padding: `${designTokens.spacing[3]} 15px`,
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.tealLine}`,
    borderRadius: designTokens.borderRadius.lg,
    fontSize: INFO_BANNER_FONT_SIZE,
    color: projectIqTokens.tealInk,
    lineHeight: String(designTokens.lineHeight.normal),
  },
  infoBannerIcon: {
    flexShrink: 0,
    color: projectIqTokens.teal,
    marginTop: "1px",
  },
  infoBannerText: {
    fontSize: designTokens.fontSize.xs,
  },
  sowAnalysisBanner: {
    marginBottom: designTokens.spacing[4],
  },
  sowAnalysisBannerContent: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  sowAnalysisBannerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
    width: "100%",
  },
  ragSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ragSectionLabel: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
  },
  ragSectionHint: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
  },
  ragCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: projectIqTokens.shadowSm,
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  ragColumn: {
    padding: "15px 18px",
    display: "flex",
    flexDirection: "column",
  },
  ragColumnBorder: {
    borderLeft: `1px solid ${projectIqTokens.line}`,
  },
  ragColumnLabel: {
    fontSize: BREADCRUMB_FONT_SIZE,
    color: projectIqTokens.muted,
    fontWeight: designTokens.fontWeight.semibold,
  },
  ragColumnStatus: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "7px",
  },
  ragDot: {
    width: RAG_DOT_SIZE,
    height: RAG_DOT_SIZE,
    minWidth: RAG_DOT_SIZE,
    borderRadius: "9999px",
    cursor: "pointer",
    border: `2px solid ${projectIqTokens.card}`,
    padding: "0",
    flexShrink: 0,
  },
  ragDotGreen: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
    boxShadow: `${RAG_GREEN_SHADOW} 0px 0px 0px 1px`,
  },
  ragDotAmber: {
    backgroundColor: tokens.colorPaletteMarigoldForeground2,
    boxShadow: `${RAG_AMBER_SHADOW} 0px 0px 0px 1px`,
  },
  ragDotRed: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    boxShadow: `${RAG_RED_SHADOW} 0px 0px 0px 1px`,
  },
  ragStatusInput: {
    color: projectIqTokens.ink,
    fontSize: RAG_STATUS_INPUT_FONT_SIZE,
    fontWeight: 800,
    minWidth: "60px",
    background: "transparent",
    border: "none",
    outline: "none",
    fontFamily: projectIqTokens.fontFamily,
    cursor: "text",
    padding: "0",
  },
  planTabBar: {
    marginBottom: designTokens.spacing[1],
  },
  bannerSlot: {
    minHeight: "1px",
  },
  raidNoteBanner: {
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    marginBottom: designTokens.spacing[1],
    fontSize: designTokens.fontSize.sm,
    color: tokens.colorNeutralForeground2,
    borderRadius: designTokens.borderRadius.sm,
  },
  statusTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: `4px 9px`,
    borderRadius: "9999px",
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.bold,
    cursor: "pointer",
    whiteSpace: "nowrap",
    backgroundColor: "transparent",
    fontFamily: projectIqTokens.fontFamily,
  },
  statusTriggerComplete: {
    border: `1px solid ${tokens.colorPaletteGreenForeground1}`,
    color: tokens.colorPaletteGreenForeground1,
  },
  statusTriggerInProgress: {
    border: `1px solid ${tokens.colorPaletteBlueForeground2}`,
    color: tokens.colorPaletteBlueForeground2,
  },
  statusTriggerUpcoming: {
    border: `1px solid ${projectIqTokens.muted}`,
    color: projectIqTokens.muted,
  },
  raidPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: RAID_PILL_FONT_SIZE,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderRadius: "5px",
    padding: "3px 7px",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  raidCategoryPill: {
    backgroundColor: projectIqTokens.bg,
    color: projectIqTokens.muted,
    border: `1px solid ${projectIqTokens.line}`,
  },
  raidSeverityHigh: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  raidSeverityMedium: {
    backgroundColor: tokens.colorPaletteMarigoldBackground1,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  raidSeverityLow: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.muted,
  },

  dialogSurface: {
    width: "520px",
    maxWidth: "90vw",
  },
  dialogContent: {
    maxHeight: "70vh",
    overflowY: "auto",
    overflowX: "hidden",
    position: "relative",
  },
  ragWeekLabel: {
    color: projectIqTokens.ink,
    fontSize: RAG_STATUS_INPUT_FONT_SIZE,
    fontWeight: 800,
  },
  scheduleDates: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    flexWrap: "wrap",
  },
  scheduleDateInput: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    background: "transparent",
    border: "none",
    outline: "none",
    fontFamily: projectIqTokens.fontFamily,
    cursor: "pointer",
    padding: "0",
    width: "90px",
  },
  scheduleDateInputError: {
    color: tokens.colorPaletteRedForeground1,
  },
  scheduleDateSep: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
  },
  scheduleDateError: {
    marginTop: "4px",
    fontSize: designTokens.fontSize.xs,
    color: tokens.colorPaletteRedForeground1,
  },
  milestoneDescription: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});
